import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Favicon from "../../images/faviconwhite.png";
import { recovery, recoveryWithCode } from "../../services/authServices";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    if (step === 2 && resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [step, resendTimer]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) return;

    setLoading(true);
    try {
      const response = await recovery({ email });
      if (response.success) {
        toast.success("Verification code sent to your email");
        setStep(2);
        setResendTimer(60);
        setCanResend(false);
      } else {
        toast.error(response.message || "Something went wrong");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Failed to send code. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const pastedCode = value.slice(0, 6).split("");
      const newCode = [...code];
      pastedCode.forEach((char, i) => {
        if (index + i < 6 && /^\d$/.test(char)) {
          newCode[index + i] = char;
        }
      });
      setCode(newCode);

      const nextIndex = Math.min(index + pastedCode.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
      return;
    }

    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const verificationCode = code.join("");
    if (verificationCode.length !== 6) return;

    setLoading(true);
    try {
      const response = await recoveryWithCode({
        email,
        code: verificationCode,
      });
      if (response.success) {
        toast.success("Code verified!");
        setStep(3);
      } else {
        toast.error(response.message || "Invalid code");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Verification failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    try {
      const response = await recovery({ email });
      if (response.success) {
        toast.success("New code sent to your email");
        setResendTimer(60);
        setCanResend(false);
        setCode(["", "", "", "", "", ""]);
      } else {
        toast.error(response.message || "Failed to resend code");
      }
    } catch (error) {
      toast.error("Failed to resend code. Please try again.");
    }
  };

  const handleUseDifferentEmail = () => {
    setStep(1);
    setCode(["", "", "", "", "", ""]);
    setResendTimer(60);
    setCanResend(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setPasswordError("");

    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const verificationCode = code.join("");
      const response = await recoveryWithCode({
        email,
        code: verificationCode,
        password,
      });

      if (response.success) {
        toast.success("Password reset successfully! Please sign in.");
        navigate("/login");
      } else {
        toast.error(response.message || "Failed to reset password");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        "Failed to reset password. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-[linear-gradient(to_top,_#38275B_0%,_#251A3C_33.33333333333333%,_#140E20_66.66666666666666%,_#000000_100%)]">
      {/* Header */}
      <div className="py-4 border-b border-slate-700 flex items-center justify-between">
        <img src={Favicon} alt="R-Link" className="h-10 w-auto ml-3" />
        <div className="flex items-center gap-2 mr-4">
          <Link
            to="/login"
            className="text-white text-sm hover:underline flex items-center gap-2"
          >
            <span>Sign In</span>
            <img src="/icons/profile.svg" alt="" className="w-5 h-5" />
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-80px)] px-4 py-8">
        {/* Modal */}
        <div className="bg-black rounded-lg shadow-2xl w-full max-w-md p-8">
          {step === 1 ? (
            // Step 1: Email Input
            <>
              <h1 className="text-white text-center text-2xl font-bold uppercase mb-4">
                RESET YOUR PASSWORD
              </h1>
              <p className="text-white text-sm mb-6">
                Enter the email linked to your R-Link account. We'll send you a
                code to verify your identity.
              </p>
              <form onSubmit={handleEmailSubmit}>
                <div className="mb-6">
                  <label
                    htmlFor="email"
                    className="block text-white text-sm font-medium mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 bg-white rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1CD760]"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1CD760] text-white font-semibold py-3 rounded-md hover:bg-[#1ab850] transition-colors disabled:opacity-50"
                >
                  {loading ? "Sending..." : "Send code"}
                </button>
              </form>
            </>
          ) : step === 2 ? (
            // Step 2: Verification Code
            <>
              <h1 className="text-white text-2xl font-bold uppercase mb-4">
                ENTER VERIFICATION CODE
              </h1>
              <p className="text-white text-sm mb-2">
                We've sent a 6-digit code to {email}
              </p>
              <p className="text-white text-sm mb-6">
                The code will expire in 10 minutes
              </p>
              <form onSubmit={handleVerify}>
                <div className="flex gap-3 mb-6 justify-center">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      className="w-14 h-14 text-center text-white bg-[#000000] border-2 border-[#615B72] rounded-md focus:outline-none focus:border-[#1CD760] text-xl font-semibold transition-colors"
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1CD760] text-white font-semibold py-3 rounded-md hover:bg-[#1ab850] transition-colors disabled:opacity-50"
                >
                  {loading ? "Verifying..." : "Verify"}
                </button>
              </form>
            </>
          ) : (
            // Step 3: Set New Password
            <>
              <h1 className="text-white text-center text-2xl font-bold uppercase mb-4">
                SET NEW PASSWORD
              </h1>
              <p className="text-white text-sm mb-6">
                Enter your new password below.
              </p>
              <form onSubmit={handlePasswordReset}>
                <div className="mb-4">
                  <label
                    htmlFor="newPassword"
                    className="block text-white text-sm font-medium mb-2"
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full px-4 py-3 bg-white rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1CD760]"
                    required
                    minLength={6}
                  />
                </div>
                <div className="mb-6">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-white text-sm font-medium mb-2"
                  >
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-4 py-3 bg-white rounded-md text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1CD760]"
                    required
                    minLength={6}
                  />
                </div>
                {passwordError && (
                  <p className="text-red-400 text-sm mb-4">{passwordError}</p>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1CD760] text-white font-semibold py-3 rounded-md hover:bg-[#1ab850] transition-colors disabled:opacity-50"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </>
          )}
        </div>

        {step === 2 && (
          <div className="text-center space-y-2 mt-12 mb-6 w-full max-w-md">
            <p className="text-white text-sm">
              Didn't get the code?{" "}
              {canResend ? (
                <button
                  onClick={handleResend}
                  className="text-[#1CD760] hover:underline"
                >
                  Resend
                </button>
              ) : (
                <span className="text-white">Resend in {resendTimer}s</span>
              )}
            </p>
            <button
              onClick={handleUseDifferentEmail}
              className="text-white text-sm underline hover:no-underline"
            >
              Use a different email
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
